import ErrorBoundary from 'pages/ErrorBoundary'
import { Role, useCompositeAuth } from 'providers/CompositeAuthProvider'
import Spinner from 'views/Spinner'
import { Navigate, useLocation } from 'react-router-dom'
import Path, { getAllowedRolesForPath } from './Path'

type ProtectedRouteProps = {
  component: React.ComponentType<object>
  roles?: Role[]
  [key: string]: any // Index signature to accept any additional props
}

const ProtectedRoute = ({ component, roles, ...args }: ProtectedRouteProps) => {
  const {
    isLoading,
    error,
    withAuthenticationRequired,
    getRole,
    isAuthenticated,
  } = useCompositeAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (error) {
    console.error(error)
    return <ErrorBoundary />
  }

  // Check if user has a role, goto error page if role is missing
  // Skip this check if user is not authenticated (e.g., during logout)
  const userRole = getRole()
  if (isAuthenticated && (userRole === Role.None || !userRole)) {
    console.error('User role is missing. Logging out for security reasons.')
    return <Navigate to={Path.error.path} replace />
  }

  // Determine roles to check: use provided roles or get from centralized config
  const rolesToCheck = roles || getAllowedRolesForPath(location.pathname)

  // Check role-based access if roles are specified
  if (rolesToCheck && rolesToCheck.length > 0) {
    const hasAccess = rolesToCheck.includes(userRole)

    if (!hasAccess) {
      // Redirect unauthorized users to home page
      return <Navigate to={Path.root.path} replace />
    }
  }

  const Component = withAuthenticationRequired(component, args)
  return <Component />
}

export default ProtectedRoute
